import { inject, Injectable } from '@angular/core';
import type { AbstractControl } from '@angular/forms';

import type { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { injectDevFlag } from '@core/dev-flags-logic';
import { DevFlag } from '@core/dev-flags-types';
import type { InterviewResult, InterviewResultAnswer } from '@core/interview-results-types';
import type { EditorJSONState } from '@core/prosemirror-types';
import type { EvaluationPanelCandidate, EvaluationPanelData } from '@recruitee/evaluations-types';

import { EvaluationFormSubmissionService } from '../evaluation-form-submission.service';
import { EvaluationAttachmentsService } from './evaluation-attachments.service';
import { EvaluationDraftService } from './evaluation-draft.service';
import { EvaluationPanelActionsService } from './evaluation-panel-actions.service';
import { QuickEvaluationFormService } from './quick-evaluation-form.service';
import { TemplateEvaluationFormService } from './template-evaluation-form.service';

interface TemplateEvaluationPayload {
  score: number | null;
  ratingNote: string | null;
  ratingNoteJson: EditorJSONState | null;
  answers: InterviewResultAnswer[];
}

@Injectable()
export class EvaluationPanelSubmitService {
  private readonly actions = inject(EvaluationPanelActionsService);
  private readonly quickForm = inject(QuickEvaluationFormService);
  private readonly templateForm = inject(TemplateEvaluationFormService);
  private readonly formSubmission = inject(EvaluationFormSubmissionService);
  private readonly attachments = inject(EvaluationAttachmentsService);
  private readonly draftService = inject(EvaluationDraftService);
  private readonly useProsemirror = injectDevFlag(DevFlag.ProsemirrorInEvaluations);

  public submit(
    data: EvaluationPanelData,
    candidate: EvaluationPanelCandidate | null,
    templateName: string,
  ): Observable<boolean> | null {
    return data.quickEvaluation
      ? this.submitQuick(data, candidate)
      : this.submitTemplate(data, candidate, templateName);
  }

  private submitQuick(
    data: EvaluationPanelData,
    candidate: EvaluationPanelCandidate | null,
  ): Observable<boolean> | null {
    if (this.actions.isSubmitting()) return null;

    const form = this.quickForm.form;
    if (form.invalid) {
      this.formSubmission.registerAttempt(form);
      return null;
    }

    const { offerId, stageId, score, ratingNote, ratingNoteJson } = form.getRawValue();
    if (score == null) return null;

    const placement = candidate?.placements.find(
      placement => placement.offerId === offerId || placement.talentPoolId === offerId,
    );

    const evaluation = {
      offerId: placement?.offerId ?? null,
      talentPoolId: placement?.talentPoolId ?? null,
      stageId: stageId ?? undefined,
      score,
      ...this.ratingNoteFields(ratingNote, ratingNoteJson),
    };

    const result$ =
      data.edit && data.resultId
        ? this.actions.updateQuickEvaluation(data.resultId, evaluation)
        : this.actions.createQuickEvaluation(data.candidateId, evaluation);

    return this.finalizeOnSuccess(result$, data.candidateId, form);
  }

  private submitTemplate(
    data: EvaluationPanelData,
    candidate: EvaluationPanelCandidate | null,
    templateName: string,
  ): Observable<boolean> | null {
    if (this.actions.isSubmitting() || this.attachments.isUploading()) return null;

    const form = this.templateForm.form;
    if (form.invalid) {
      this.formSubmission.registerAttempt(form);
      return null;
    }

    const { score, ratingNote, ratingNoteJson } = form.getRawValue();
    const answers = this.templateForm.getAnswersPayload();
    const payload: TemplateEvaluationPayload = { score, ratingNote, ratingNoteJson, answers };

    const result$ =
      data.edit && data.resultId
        ? this.actions.updateTemplateEvaluation(data.resultId, {
            score: score ?? undefined,
            ...this.ratingNoteFields(ratingNote, ratingNoteJson),
            interviewResultAnswers: answers,
          })
        : this.createTemplateEvaluation(data, candidate, templateName, payload);

    return this.finalizeOnSuccess(result$, data.candidateId, form);
  }

  private createTemplateEvaluation(
    data: EvaluationPanelData,
    candidate: EvaluationPanelCandidate | null,
    templateName: string,
    payload: TemplateEvaluationPayload,
  ): Observable<boolean> {
    const placement = candidate?.placements.find(
      placement => placement.offerId === data.offerId || placement.talentPoolId === data.offerId,
    );

    return this.actions.createTemplateEvaluation(data.candidateId, {
      offerId: placement?.offerId ?? null,
      talentPoolId: placement?.talentPoolId ?? null,
      stageId: placement?.stageId ?? undefined,
      score: payload.score ?? undefined,
      ...this.ratingNoteFields(payload.ratingNote, payload.ratingNoteJson),
      interviewTemplateId: data.templateId?.toString(),
      interviewTemplateName: templateName,
      interviewResultRequestId: data.resultRequestId,
      interviewResultAnswers: payload.answers,
    });
  }

  private ratingNoteFields(
    ratingNote: string | null,
    ratingNoteJson: EditorJSONState | null,
  ): Partial<Pick<InterviewResult, 'ratingNote' | 'ratingNoteJson'>> {
    return this.useProsemirror ? { ratingNoteJson } : { ratingNote };
  }

  private finalizeOnSuccess(
    result$: Observable<boolean>,
    candidateId: number,
    form: AbstractControl,
  ): Observable<boolean> {
    return result$.pipe(
      tap(success => {
        if (!success) return;

        this.draftService.clearDraft(candidateId);
        form.markAsPristine();
      }),
    );
  }
}
